import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudUpload, RotateCcw, Trash2 } from "lucide-react";
import { pendingCount, authPaused, subscribeOps, processOps } from "../lib/tourOpQueue";
import { pendingReceipts, subscribeQueue, retryReceipt, removeReceipt } from "../lib/receiptUploadQueue";

/**
 * 2026-10-25 (S1): Det, der venter på telefonen - for ALLE handlinger, ikke kun
 * stempler: start, stop, billeder, afslutninger og vejesedler. En vejeseddel,
 * serveren har afvist, står her med "Send igen" og "Fjern" (før forsvandt den
 * efter 5 forsøg uden at sige det).
 */
export default function PendingQueueBar() {
  const read = () => ({ ops: pendingCount(), paused: authPaused(), receipts: pendingReceipts() });
  const [s, setS] = useState(read);
  useEffect(() => {
    const refresh = () => setS(read());
    const offOps = subscribeOps(refresh);
    const offRc = subscribeQueue((_n, ev) => {
      refresh();
      if (ev?.type === "full") toast.error(ev.message, { duration: 12000 });
      if (ev?.type === "failed") toast.error(`En vejeseddel blev afvist: ${ev.item?.lastError || "serveren sagde nej"}. Se "Venter på at blive sendt".`, { duration: 12000 });
    });
    const iv = setInterval(refresh, 10000);
    return () => { offOps(); offRc(); clearInterval(iv); };
  }, []);
  const waiting = s.receipts.filter((r) => !r.permanentFailure).length;
  const failed = s.receipts.filter((r) => r.permanentFailure);
  if (!s.ops && !waiting && !failed.length) return null;
  const parts = [s.ops ? `${s.ops} ${s.ops === 1 ? "handling" : "handlinger"}` : "", waiting ? `${waiting} ${waiting === 1 ? "vejeseddel" : "vejesedler"}` : ""].filter(Boolean);
  return (
    <div className="bg-sky-50 text-sky-900 dark:bg-sky-950/60 dark:text-sky-100 border-b border-sky-200 dark:border-sky-900 px-4 py-2 text-xs space-y-1.5" data-testid="pending-queue-bar">
      {parts.length > 0 && (
        <div className="flex items-center gap-2 font-semibold">
          <CloudUpload className="w-4 h-4 shrink-0" />
          <span className="flex-1">{parts.join(" og ")} venter på at blive sendt
            {s.paused ? " - log ind igen, så sendes de." : typeof navigator !== "undefined" && navigator.onLine === false ? " - sendes, når der er net." : "."}</span>
          {!s.paused && <button type="button" className="underline" onClick={() => { processOps(); }}>Send nu</button>}
        </div>
      )}
      {failed.map((r) => (
        <div key={r.queueId} className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 text-red-900 dark:bg-red-950/50 dark:text-red-100 px-2 py-1.5" data-testid="pending-receipt-failed">
          <span className="flex-1 min-w-[180px]">Vejeseddel{r.final_weight_kg ? ` (${String(r.final_weight_kg).replace(".", ",")} kg)` : ""} blev ikke modtaget: {r.lastError || "serveren sagde nej"}</span>
          <button type="button" className="inline-flex items-center gap-1 font-semibold underline" onClick={() => retryReceipt(r.queueId)}><RotateCcw className="w-3.5 h-3.5" />Send igen</button>
          <button type="button" className="inline-flex items-center gap-1 text-red-700 dark:text-red-300" aria-label="Fjern vejesedlen"
            onClick={() => { if (window.confirm("Fjern vejesedlen fra telefonen? Billedet bliver ikke sendt.")) removeReceipt(r.queueId); }}><Trash2 className="w-3.5 h-3.5" />Fjern</button>
        </div>
      ))}
    </div>
  );
}
